/**
 * ModernTemplate - Clean minimalist with bold typography and generous whitespace
 * Visual moderno com hero grande, grid layouts contemporâneos e animações sutis
 */

import { Sparkles, Award, Target, CheckCircle, FileText, Image, Hash } from 'lucide-react';
import { SantinhoTemplateProps, formatMandate, hasSocialLinks } from './types';
import { SocialLinks } from './SocialLinks';
import { GallerySection } from './GallerySection';
import { ApprovedProjectsSection } from './ApprovedProjectsSection';
import { ContactRegistrationButton } from '../ContactRegistrationButton';
import { WebchatWidget } from '../../webchat';

export function ModernTemplate({ tenant }: SantinhoTemplateProps) {
  const { publicPage, theme, name } = tenant;
  const { title, description, content, socialLinks } = publicPage;

  return (
    <div
      className="min-h-screen bg-white"
      style={{ fontFamily: theme.fontFamily || 'inherit' }}
    >
      {/* Hero Section */}
      <header className="relative h-80 md:h-[28rem] overflow-hidden">
        {content.coverPhoto ? (
          <img
            src={content.coverPhoto}
            alt={name}
            className="w-full h-full object-cover scale-105 transition-transform duration-700"
          />
        ) : (
          <div
            className="w-full h-full"
            style={{
              background: `linear-gradient(145deg, ${theme.primaryColor} 0%, ${theme.secondaryColor} 60%, ${theme.primaryColor}cc 100%)`,
            }}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />

        {/* Decorative geometric shape */}
        <div
          className="absolute bottom-0 right-0 w-64 h-64 opacity-10 rounded-tl-full"
          style={{ backgroundColor: 'white' }}
        />
      </header>

      {/* Profile Floating Card */}
      <div className="container mx-auto px-4 relative z-10">
        <div className="flex flex-col items-center -mt-28">
          {/* Candidate Photo */}
          <div className="relative">
            {content.candidatePhoto ? (
              <img
                src={content.candidatePhoto}
                alt={name}
                className="w-44 h-44 md:w-52 md:h-52 rounded-full object-cover border-[6px] border-white shadow-2xl"
              />
            ) : (
              <div
                className="w-44 h-44 md:w-52 md:h-52 rounded-full border-[6px] border-white shadow-2xl flex items-center justify-center text-6xl font-bold text-white"
                style={{ backgroundColor: theme.primaryColor }}
              >
                {name.charAt(0)}
              </div>
            )}
            {/* Status ring */}
            <div
              className="absolute inset-0 rounded-full ring-4 ring-offset-4 ring-white/60"
            />
          </div>

          {/* Candidate Number Badge */}
          {content.candidateNumber && (
            <div className="mt-5">
              <div
                className="flex items-center gap-2 px-7 py-3 rounded-2xl text-white font-bold text-3xl shadow-lg"
                style={{
                  background: `linear-gradient(135deg, ${theme.primaryColor}, ${theme.secondaryColor})`,
                }}
              >
                <Hash className="w-6 h-6 opacity-80" />
                <span className="tracking-widest">{content.candidateNumber}</span>
              </div>
            </div>
          )}

          {/* Title and Description */}
          <div className="text-center mt-6 max-w-2xl">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
              {title || name}
            </h1>
            {description && (
              <p className="mt-3 text-xl text-gray-500 leading-relaxed">{description}</p>
            )}
          </div>

          {/* Social Links */}
          {hasSocialLinks(socialLinks) && (
            <div className="mt-7">
              <SocialLinks links={socialLinks} color={theme.primaryColor} />
            </div>
          )}

          {/* Divider */}
          <div className="flex items-center gap-4 mt-10 w-full max-w-md">
            <div className="flex-1 h-px bg-gray-200" />
            <Sparkles className="w-5 h-5 text-gray-300" />
            <div className="flex-1 h-px bg-gray-200" />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto space-y-20">

          {/* Biography */}
          {content.biography && (
            <section>
              <div className="flex items-center gap-3 mb-8">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${theme.primaryColor}15` }}
                >
                  <Sparkles className="w-5 h-5" style={{ color: theme.primaryColor }} />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Sobre</h2>
              </div>
              <div className="prose prose-lg max-w-none text-gray-600 leading-relaxed">
                {content.biography.split('\n').filter(Boolean).map((p, i) => (
                  <p key={i} className="mb-4 last:mb-0">{p}</p>
                ))}
              </div>
            </section>
          )}

          {/* Mandates */}
          {content.mandates && content.mandates.length > 0 && (
            <section>
              <div className="flex items-center gap-3 mb-8">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${theme.primaryColor}15` }}
                >
                  <Award className="w-5 h-5" style={{ color: theme.primaryColor }} />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Experiência Política</h2>
              </div>
              <div className="flex flex-wrap gap-3">
                {content.mandates.map((mandate, i) => (
                  <span
                    key={i}
                    className="px-5 py-2.5 rounded-full text-white text-sm font-medium shadow-sm"
                    style={{ backgroundColor: theme.primaryColor }}
                  >
                    {formatMandate(mandate)}
                  </span>
                ))}
              </div>
            </section>
          )}

          {/* Proposals */}
          {content.proposals && content.proposals.length > 0 && (
            <section>
              <div className="flex items-center gap-3 mb-8">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${theme.primaryColor}15` }}
                >
                  <Target className="w-5 h-5" style={{ color: theme.primaryColor }} />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Propostas</h2>
              </div>
              <div className="grid md:grid-cols-2 gap-5">
                {content.proposals.map((proposal, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-4 p-5 rounded-2xl bg-gray-50 hover:bg-gray-100 transition-colors border border-transparent hover:border-gray-200"
                  >
                    <div
                      className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-sm shadow-sm"
                      style={{ backgroundColor: theme.primaryColor }}
                    >
                      {i + 1}
                    </div>
                    <p className="text-gray-700 pt-1.5 leading-relaxed">{proposal}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Achievements */}
          {content.achievements && content.achievements.length > 0 && (
            <section>
              <div className="flex items-center gap-3 mb-8">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${theme.primaryColor}15` }}
                >
                  <CheckCircle className="w-5 h-5" style={{ color: theme.primaryColor }} />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Conquistas</h2>
              </div>
              <div className="grid md:grid-cols-2 gap-5">
                {content.achievements.map((achievement, i) => (
                  <div
                    key={i}
                    className="p-5 rounded-2xl border-2 bg-white hover:shadow-md transition-shadow"
                    style={{ borderColor: `${theme.primaryColor}30` }}
                  >
                    <div className="flex items-start gap-3">
                      <CheckCircle
                        className="w-5 h-5 mt-0.5 flex-shrink-0"
                        style={{ color: theme.primaryColor }}
                      />
                      <p className="text-gray-700 leading-relaxed">{achievement}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Approved Projects */}
          {content.approvedProjects && content.approvedProjects.length > 0 && (
            <section>
              <div className="flex items-center gap-3 mb-8">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${theme.primaryColor}15` }}
                >
                  <FileText className="w-5 h-5" style={{ color: theme.primaryColor }} />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Projetos Aprovados</h2>
              </div>
              <ApprovedProjectsSection
                projects={content.approvedProjects}
                primaryColor={theme.primaryColor}
                variant="timeline"
              />
            </section>
          )}

          {/* Gallery */}
          {content.gallery && content.gallery.length > 0 && (
            <section>
              <div className="flex items-center gap-3 mb-8">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${theme.primaryColor}15` }}
                >
                  <Image className="w-5 h-5" style={{ color: theme.primaryColor }} />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Galeria</h2>
              </div>
              <GallerySection images={content.gallery} />
            </section>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer
        className="py-10 mt-8"
        style={{
          background: `linear-gradient(135deg, ${theme.primaryColor} 0%, ${theme.secondaryColor} 100%)`,
        }}
      >
        <div className="container mx-auto px-4 text-center">
          <p className="text-white/70 text-sm">
            Página criada com{' '}
            <a href="https://campanhaflow.com.br" className="text-white hover:underline font-medium">
              CampanhaFlow
            </a>
          </p>
        </div>
      </footer>

      {/* Contact Registration Button */}
      <ContactRegistrationButton />

      {/* Webchat widget */}
      <WebchatWidget tenant={tenant} />
    </div>
  );
}
